import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LanguageProvider, useLanguage } from '../context/LanguageContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Toaster } from '../components/ui/sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Calendar,
  MapPin,
  Users,
  Tent,
  CheckCircle2,
  XCircle,
  Send,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_FORM = {
  child_first_name: '',
  child_last_name: '',
  child_birth_date: '',
  child_gender: '',
  child_phone: '',
  child_email: '',
  allergies: '',
  dietary_restrictions: '',
  health_notes: '',
  parent_name: '',
  parent_phone: '',
  parent_email: '',
  address: '',
  consent_data_usage: false,
  consent_photo_website: false,
  consent_photo_lovec: false,
  consent_photo_slivniski: false,
  notes: '',
};

const calcAge = (dateStr) => {
  if (!dateStr) return null;
  const bd = new Date(dateStr);
  if (isNaN(bd.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - bd.getFullYear();
  const m = today.getMonth() - bd.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < bd.getDate())) age--;
  return age;
};

const CampCard = ({ camp, onRegister }) => {
  const { language } = useLanguage();
  const [showGallery, setShowGallery] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  
  const active = camp.is_active && camp.registration_open;
  const isFull = camp.is_full;
  const accepting = active; // backend accepts even when full (waitlist)
  const buttonLabel = !accepting
    ? (language === 'sl' ? 'Prijave zaprte' : 'Registration closed')
    : isFull
      ? (language === 'sl' ? 'Prijava na čakalno listo' : 'Join waiting list')
      : (language === 'sl' ? 'Prijavi otroka' : 'Register a child');
  const slots = (camp.max_participants || 0) - (camp.current_count || 0);
  
  const allImages = [
    ...(camp.image ? [camp.image] : []),
    ...(camp.images || [])
  ];
  
  const openGallery = (idx) => {
    setGalleryIndex(idx);
    setShowGallery(true);
  };
  
  return (
    <>
      <Card
        data-testid={`camp-card-${camp.id}`}
        className="bg-stone-800/60 border-stone-700/60 p-6 hover:border-emerald-600/50 transition-colors"
      >
        <div className="flex flex-col md:flex-row gap-6">
          {camp.image && (
            <img
              src={camp.image}
              alt={camp.title_sl}
              className="w-full md:w-56 h-40 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => openGallery(0)}
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <span className="text-3xl font-bold text-emerald-400">{camp.year}</span>
              <h3 className="text-xl font-semibold text-white">
                {language === 'sl' ? camp.title_sl : camp.title_en}
              </h3>
              {isFull && accepting && (
                <span
                  data-testid="camp-full-badge"
                  className="px-2 py-0.5 text-xs rounded-full bg-amber-700/30 text-amber-300 border border-amber-600/40"
                >
                  {language === 'sl' ? 'Tabor poln · čakalna lista' : 'Full · waiting list'}
                </span>
              )}
            </div>
            {(language === 'sl' ? camp.description_sl : camp.description_en) && (
              <p className="text-stone-300 mb-3 text-sm">
                {language === 'sl' ? camp.description_sl : camp.description_en}
              </p>
            )}
            <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-stone-400 mb-4">
              {(camp.start_date || camp.end_date) && (
                <span className="inline-flex items-center gap-1.5">
                  <Calendar className="w-4 h-4 text-emerald-500" />
                  {camp.start_date} {camp.end_date ? `– ${camp.end_date}` : ''}
                </span>
              )}
              {camp.location && (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="w-4 h-4 text-emerald-500" />
                  {camp.location}
                </span>
              )}
              <span className="inline-flex items-center gap-1.5">
                <Users className="w-4 h-4 text-emerald-500" />
                {isFull
                  ? (language === 'sl' ? 'Polno' : 'Full')
                  : `${slots > 0 ? slots : 0} ${language === 'sl' ? 'prostih mest' : 'spots left'}`}
                {camp.waitlist_count > 0 && (
                  <span className="text-amber-400 ml-1">
                    · {camp.waitlist_count} {language === 'sl' ? 'na čakanju' : 'waiting'}
                  </span>
                )}
              </span>
            </div>
            
            {/* Gallery thumbnails */}
            {allImages.length > 1 && (
              <div className="flex gap-2 mb-4 flex-wrap">
                {allImages.slice(0, 5).map((img, idx) => (
                  <img
                    key={idx}
                    src={img}
                    alt={`${camp.title_sl} ${idx + 1}`}
                    className="w-12 h-12 object-cover rounded cursor-pointer border border-stone-700 hover:border-emerald-500 transition-colors"
                    onClick={() => openGallery(idx)}
                  />
                ))}
                {allImages.length > 5 && (
                  <button
                    onClick={() => openGallery(5)}
                    className="w-12 h-12 rounded bg-stone-700/60 flex items-center justify-center text-stone-300 text-sm hover:bg-stone-700 transition-colors"
                  >
                    +{allImages.length - 5}
                  </button>
                )}
              </div>
            )}
            
            <Button
              data-testid={`register-camp-btn-${camp.id}`}
              disabled={!accepting}
              onClick={() => onRegister(camp)}
              className={
                isFull
                  ? 'bg-amber-600 hover:bg-amber-500 disabled:opacity-50 disabled:cursor-not-allowed'
                  : 'bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed'
              }
            >
              {accepting ? (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  {buttonLabel}
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4 mr-2" />
                  {buttonLabel}
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>
      
      {/* Gallery Modal */}
      {showGallery && allImages.length > 0 && (
        <Dialog open={showGallery} onOpenChange={setShowGallery}>
          <DialogContent className="bg-stone-900/95 border-stone-700 max-w-4xl p-2">
            <div className="relative">
              <img
                src={allImages[galleryIndex]}
                alt={`${camp.title_sl} ${galleryIndex + 1}`}
                className="w-full max-h-[70vh] object-contain rounded-lg"
              />
              {allImages.length > 1 && (
                <>
                  <button
                    onClick={() => setGalleryIndex((galleryIndex - 1 + allImages.length) % allImages.length)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center text-white"
                  >
                    ‹
                  </button>
                  <button
                    onClick={() => setGalleryIndex((galleryIndex + 1) % allImages.length)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center text-white"
                  >
                    ›
                  </button>
                </>
              )}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 px-3 py-1 rounded-full text-white text-sm">
                {galleryIndex + 1} / {allImages.length}
              </div>
            </div>
            {allImages.length > 1 && (
              <div className="flex gap-2 justify-center mt-3 flex-wrap px-2 pb-2">
                {allImages.map((img, idx) => (
                  <img
                    key={idx}
                    src={img}
                    alt={`Thumbnail ${idx + 1}`}
                    className={`w-14 h-14 object-cover rounded cursor-pointer transition-all ${
                      idx === galleryIndex 
                        ? 'ring-2 ring-emerald-500 opacity-100' 
                        : 'opacity-60 hover:opacity-100'
                    }`}
                    onClick={() => setGalleryIndex(idx)}
                  />
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

const RegistrationDialog = ({ camp, open, onOpenChange, onSuccess }) => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  if (!camp) return null;

  const age = calcAge(formData.child_birth_date);
  const ageValid = age !== null && age >= 7 && age <= 14;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.consent_data_usage) {
      toast.error('Soglasje za obdelavo osebnih podatkov je obvezno');
      return;
    }
    if (!ageValid) {
      toast.error('Otrok mora biti star med 7 in 14 let');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/camps/${camp.id}/register`, {
        ...formData,
        camp_id: camp.id,
      });
      if (data?.status === 'waitlist') {
        toast.success('Prijava na čakalno listo je bila uspešno oddana. Obvestili vas bomo, če se sprosti mesto.');
      } else {
        toast.success('Prijava je bila uspešno oddana!');
      }
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Napaka pri oddaji prijave';
      toast.error(typeof detail === 'string' ? detail : 'Napaka pri oddaji');
    } finally {
      setSubmitting(false);
    }
  };

  const update = (field, value) => setFormData((p) => ({ ...p, [field]: value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-stone-900 border-stone-700 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white text-xl">
            Prijava na Lovski tabor {camp.year}
          </DialogTitle>
          <DialogDescription className="text-stone-400">
            {camp.title_sl}
            {camp.start_date && ` · ${camp.start_date}${camp.end_date ? ` – ${camp.end_date}` : ''}`}
            {camp.location && ` · ${camp.location}`}
          </DialogDescription>
        </DialogHeader>

        {camp.is_full && (
          <div
            data-testid="waitlist-banner"
            className="rounded-lg border border-amber-600/40 bg-amber-700/15 px-4 py-3 text-sm text-amber-200"
          >
            <b>Tabor je trenutno poln.</b> Prijavo lahko oddate na{' '}
            <b>čakalno listo</b> — če kdo od prijavljenih odpove, vas bomo o sproščenem mestu
            obvestili po e-pošti.
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="camp-registration-form">
          {/* Child section */}
          <fieldset className="border border-stone-700/50 rounded-lg p-4 space-y-3">
            <legend className="px-2 text-emerald-400 text-sm font-semibold">Podatki o otroku</legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-stone-300">Ime *</Label>
                <Input
                  data-testid="reg-child-firstname"
                  required
                  value={formData.child_first_name}
                  onChange={(e) => update('child_first_name', e.target.value)}
                  className="bg-stone-800/60 border-stone-700 text-white"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-stone-300">Priimek *</Label>
                <Input
                  data-testid="reg-child-lastname"
                  required
                  value={formData.child_last_name}
                  onChange={(e) => update('child_last_name', e.target.value)}
                  className="bg-stone-800/60 border-stone-700 text-white"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-stone-300">Rojstni datum *</Label>
                <Input
                  data-testid="reg-child-birthdate"
                  required
                  type="date"
                  value={formData.child_birth_date}
                  onChange={(e) => update('child_birth_date', e.target.value)}
                  className="bg-stone-800/60 border-stone-700 text-white"
                />
                {formData.child_birth_date && age !== null && (
                  <p className={`text-xs ${ageValid ? 'text-emerald-400' : 'text-red-400'}`}>
                    Starost: {age} let {ageValid ? '✓' : '(dovoljeno 7–14 let)'}
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-stone-300">Spol *</Label>
                <Select
                  value={formData.child_gender || undefined}
                  onValueChange={(v) => update('child_gender', v)}
                >
                  <SelectTrigger
                    data-testid="reg-child-gender"
                    className="bg-stone-800/60 border-stone-700 text-white"
                  >
                    <SelectValue placeholder="Izberi spol" />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-900 border-stone-700 text-white">
                    <SelectItem value="M">Moški</SelectItem>
                    <SelectItem value="Ž">Ženski</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-stone-300">Telefon otroka</Label>
                <Input
                  data-testid="reg-child-phone"
                  value={formData.child_phone}
                  onChange={(e) => update('child_phone', e.target.value)}
                  className="bg-stone-800/60 border-stone-700 text-white"
                  placeholder="(neobvezno)"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-stone-300">Email otroka (neobvezno)</Label>
              <Input
                data-testid="reg-child-email"
                type="email"
                value={formData.child_email}
                onChange={(e) => update('child_email', e.target.value)}
                className="bg-stone-800/60 border-stone-700 text-white"
              />
            </div>
          </fieldset>

          {/* Health */}
          <fieldset className="border border-stone-700/50 rounded-lg p-4 space-y-3">
            <legend className="px-2 text-emerald-400 text-sm font-semibold">
              Zdravje in prehrana
            </legend>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-stone-300">Alergije</Label>
                <Textarea
                  data-testid="reg-allergies"
                  value={formData.allergies}
                  onChange={(e) => update('allergies', e.target.value)}
                  className="bg-stone-800/60 border-stone-700 text-white min-h-[70px]"
                  placeholder="npr. oreški, čebelji pik..."
                />
              </div>
              <div className="space-y-1">
                <Label className="text-stone-300">Prehranske omejitve</Label>
                <Textarea
                  data-testid="reg-dietary"
                  value={formData.dietary_restrictions}
                  onChange={(e) => update('dietary_restrictions', e.target.value)}
                  className="bg-stone-800/60 border-stone-700 text-white min-h-[70px]"
                  placeholder="npr. brez glutena, vegetarijanec..."
                />
              </div>
              <div className="space-y-1">
                <Label className="text-stone-300">Zdravstvene omejitve</Label>
                <Textarea
                  data-testid="reg-health"
                  value={formData.health_notes}
                  onChange={(e) => update('health_notes', e.target.value)}
                  className="bg-stone-800/60 border-stone-700 text-white min-h-[70px]"
                  placeholder="npr. astma, redna zdravila..."
                />
              </div>
            </div>
          </fieldset>

          {/* Parent */}
          <fieldset className="border border-stone-700/50 rounded-lg p-4 space-y-3">
            <legend className="px-2 text-emerald-400 text-sm font-semibold">
              Kontakt starša / skrbnika
            </legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-stone-300">Ime in priimek *</Label>
                <Input
                  data-testid="reg-parent-name"
                  required
                  value={formData.parent_name}
                  onChange={(e) => update('parent_name', e.target.value)}
                  className="bg-stone-800/60 border-stone-700 text-white"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-stone-300">Telefon *</Label>
                <Input
                  data-testid="reg-parent-phone"
                  required
                  value={formData.parent_phone}
                  onChange={(e) => update('parent_phone', e.target.value)}
                  className="bg-stone-800/60 border-stone-700 text-white"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-stone-300">Email *</Label>
                <Input
                  data-testid="reg-parent-email"
                  required
                  type="email"
                  value={formData.parent_email}
                  onChange={(e) => update('parent_email', e.target.value)}
                  className="bg-stone-800/60 border-stone-700 text-white"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-stone-300">Naslov</Label>
                <Input
                  data-testid="reg-parent-address"
                  value={formData.address}
                  onChange={(e) => update('address', e.target.value)}
                  className="bg-stone-800/60 border-stone-700 text-white"
                  placeholder="Ulica, kraj"
                />
              </div>
            </div>
          </fieldset>

          {/* Consents */}
          <fieldset className="border border-stone-700/50 rounded-lg p-4 space-y-3">
            <legend className="px-2 text-emerald-400 text-sm font-semibold">Soglasja</legend>

            <label className="flex items-start gap-3 p-2 rounded hover:bg-stone-800/40 cursor-pointer">
              <Checkbox
                data-testid="reg-consent-data"
                checked={formData.consent_data_usage}
                onCheckedChange={(v) => update('consent_data_usage', !!v)}
                className="mt-0.5 border-stone-600 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
              />
              <span className="text-sm text-stone-300">
                <span className="text-red-400">*</span> Soglašam, da Lovska družina Rakek uporabi
                navedene osebne podatke za <b className="text-white">organizacijo in izvedbo tabora</b>{' '}
                (obvezno).
              </span>
            </label>

            <label className="flex items-start gap-3 p-2 rounded hover:bg-stone-800/40 cursor-pointer">
              <Checkbox
                data-testid="reg-consent-website"
                checked={formData.consent_photo_website}
                onCheckedChange={(v) => update('consent_photo_website', !!v)}
                className="mt-0.5 border-stone-600 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
              />
              <span className="text-sm text-stone-300">
                Dovoljujem objavo fotografij otroka na{' '}
                <b className="text-white">spletni strani LD Rakek</b> za izobraževalne in
                predstavitvene namene.
              </span>
            </label>

            <label className="flex items-start gap-3 p-2 rounded hover:bg-stone-800/40 cursor-pointer">
              <Checkbox
                data-testid="reg-consent-lovec"
                checked={formData.consent_photo_lovec}
                onCheckedChange={(v) => update('consent_photo_lovec', !!v)}
                className="mt-0.5 border-stone-600 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
              />
              <span className="text-sm text-stone-300">
                Dovoljujem objavo fotografij v glasilu <b className="text-white">Lovec</b>.
              </span>
            </label>

            <label className="flex items-start gap-3 p-2 rounded hover:bg-stone-800/40 cursor-pointer">
              <Checkbox
                data-testid="reg-consent-slivniski"
                checked={formData.consent_photo_slivniski}
                onCheckedChange={(v) => update('consent_photo_slivniski', !!v)}
                className="mt-0.5 border-stone-600 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
              />
              <span className="text-sm text-stone-300">
                Dovoljujem objavo fotografij v glasilu <b className="text-white">Slivniški pogledi</b>.
              </span>
            </label>
          </fieldset>

          <div className="space-y-1">
            <Label className="text-stone-300">Dodatne opombe</Label>
            <Textarea
              data-testid="reg-notes"
              value={formData.notes}
              onChange={(e) => update('notes', e.target.value)}
              className="bg-stone-800/60 border-stone-700 text-white"
              placeholder="Karkoli želite sporočiti organizatorjem..."
            />
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-stone-700 text-stone-300 hover:bg-stone-800"
            >
              Prekliči
            </Button>
            <Button
              type="submit"
              data-testid="submit-registration-btn"
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-500"
            >
              {submitting ? 'Pošiljam…' : (<><CheckCircle2 className="w-4 h-4 mr-2" />Oddaj prijavo</>)}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const LovskiTaborInner = () => {
  const { language } = useLanguage();
  const [camps, setCamps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCamp, setSelectedCamp] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchCamps = async () => {
    try {
      const { data } = await axios.get(`${API}/camps`);
      setCamps((data || []).filter((c) => c.is_active));
    } catch (err) {
      console.error('Error fetching camps:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/camps`);
        if (!cancelled) setCamps((data || []).filter((c) => c.is_active));
      } catch (err) {
        console.error('Error fetching camps:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleRegister = (camp) => {
    setSelectedCamp(camp);
    setDialogOpen(true);
  };

  return (
    <div className="App bg-stone-900 min-h-screen">
      <Header />
      <main className="pt-28 pb-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-3">
            <Tent className="w-7 h-7 text-emerald-400" />
            <h1 className="text-4xl sm:text-5xl font-bold text-white">
              {language === 'sl' ? 'Lovski tabor' : 'Hunting Camp'}
            </h1>
          </div>
          <p className="text-stone-300 mb-10 text-base max-w-3xl">
            {language === 'sl'
              ? 'Lovska družina Rakek vsako leto organizira tabor za otroke od 7. do 14. leta starosti. Otroci spoznajo naravo, divjad, gozd in osnove odgovornega lovstva pod vodstvom izkušenih članov.'
              : 'Hunting Society Rakek organizes an annual camp for children aged 7–14. Participants explore nature, wildlife, the forest and the basics of responsible hunting under the guidance of experienced members.'}
          </p>

          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : camps.length === 0 ? (
            <Card className="bg-stone-800/40 border-stone-700/50 p-10 text-center">
              <Tent className="w-12 h-12 text-stone-600 mx-auto mb-3" />
              <p className="text-stone-400">
                {language === 'sl'
                  ? 'Trenutno ni razpisanih taborov. Spremljajte našo stran za novosti.'
                  : 'No camps are currently announced. Stay tuned for updates.'}
              </p>
            </Card>
          ) : (
            <div className="space-y-5" data-testid="camps-list">
              {camps.map((camp) => (
                <CampCard key={camp.id} camp={camp} onRegister={handleRegister} />
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
      <Toaster position="top-right" richColors />

      {dialogOpen && selectedCamp && (
        <RegistrationDialog
          key={selectedCamp.id}
          camp={selectedCamp}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onSuccess={fetchCamps}
        />
      )}
    </div>
  );
};

const LovskiTaborPage = () => (
  <LanguageProvider>
    <LovskiTaborInner />
  </LanguageProvider>
);

export default LovskiTaborPage;
